import fetch from 'isomorphic-fetch';
import { has } from 'lodash';
import axios from 'axios';
import { API_ERROR } from '../../constants/actions';
import { localToRemoteUrl } from '../../helpers/urls';
import {
  getAccessToken,
  getOktaAccessToken,
  shouldRenewAccessToken,
  NATIVE_AUTH_FAILURE,
} from '../../helpers/localStorage';
import { shouldUseNativeAuth } from '../../helpers/platform';
import { trackAuthEvent, datadogRumActions } from '../../helpers/datadog-rum';
import { deauthUserAndUnsubscribeTopics } from '../auth-utils';
import { getCreationSource } from '../../helpers/creation-source';
import { IN_TESTING_MODE, TEST_AUTH_TOKEN } from '../../tests/constants';
import { serializeError } from '@app/utils/serialize-error';
import i18n from '@app/i18n';

const { CancelToken } = axios;
const UNAUTH = 'UNAUTH';
const DEBUG = false;

export function validateAuthErrorAndDispatchDeauth(error, dispatch) {
  // Only an HTTP 401/403 (UNAUTH) immediately deauths here. This helper is
  // called by many direct doRequest callers (profile/job/users) that have NO
  // retry-before-deauth, so it must NOT deauth on NATIVE_AUTH_FAILURE — a
  // transient native token hiccup there would force logout without recovery.
  // apiCall's recovery path handles NATIVE_AUTH_FAILURE explicitly (it calls
  // fail(UNAUTH) only after the forced refresh also fails).
  if (error === UNAUTH) {
    dispatch(deauthUserAndUnsubscribeTopics());
  }
}

export async function makeRequest(opts) {
  const url = localToRemoteUrl(opts.endpoint, opts.host);

  let authTok = TEST_AUTH_TOKEN;
  if (!IN_TESTING_MODE) {
    // this ternary is used here to avoid a promise when possible
    // so that the api calls can be correctly parallelized,
    // please do not abstract the line below to a single promise helper function
    authTok = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  const includeCreationSource =
    opts.includeCreationSource !== undefined
      ? opts.includeCreationSource
      : true;

  const headers = {
    Authorization: authTok,
    'Accept-Language': i18n.language || 'en',
    ...(includeCreationSource
      ? { 'x-marley-creation-source': getCreationSource() }
      : {}),
  };

  const options = {
    url,
    method: opts.method || 'GET',
    headers,
  };

  if (has(opts, 'body')) {
    options.data = opts.body;
  }

  return axios(options);
}

export async function doRequest(opts) {
  if (DEBUG) console.log(opts);
  const url = localToRemoteUrl(opts.endpoint, opts.host);
  if (DEBUG) console.log('doRequest url', url);
  const includeCreationSource =
    opts.includeCreationSource !== undefined
      ? opts.includeCreationSource
      : true;

  let authTok = null;
  if (!opts.noAuth) {
    // `authTokenOverride` lets a caller supply an already-acquired token (e.g.
    // apiCall's retry passes the token from its forced refresh) so we don't
    // re-acquire — and so the retry provably uses that fresh token.
    // Do NOT move this ternary into a helper function
    // because nested Promises with Redux dispatch can delay API calls.
    // Safe to refactor only after migrating away from Redux.
    authTok =
      opts.authTokenOverride ??
      (shouldRenewAccessToken()
        ? await getAccessToken()
        : getOktaAccessToken());
  }

  const headers = opts.headers || {
    'content-type': 'application/json',
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    Authorization: authTok,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: 0,
    ...(includeCreationSource
      ? { 'x-marley-creation-source': getCreationSource() }
      : {}),
  };

  /**
   * This line is necessary to be backwards-compatible with old requests
   * that pass their own headers. This is an attempt to load the tokens
   * in the least amount of spots
   */
  headers.Authorization = authTok;
  headers['Accept-Language'] = i18n.language || 'en';

  const payload = {
    method: opts.method || 'GET',
    mode: opts.mode,
    headers: new Headers(headers),
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    ...(opts.signal && { signal: opts.signal }),
  };
  const actionName = opts.action;
  const { dispatch } = opts;

  return fetch(url, payload)
    .then((response) => {
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = response.json();
      } else if (contentType && contentType.includes('image')) {
        responseData = response.blob();
      } else {
        responseData = response.text();
      }
      if (response.status >= 300) {
        if (response.status === 401 || response.status === 403) {
          return Promise.reject(UNAUTH);
        }

        return responseData.then((data) => Promise.reject(data));
      }
      return responseData;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log('fetch error in doRequest', error);
      const errorPayload = {
        error: serializeError(error),
      };
      if (dispatch) {
        if (opts.passBodyOnError) {
          dispatch({
            type: `REQUEST_${actionName}_ERROR`,
            payload: { ...errorPayload, body: opts?.body },
          });
        } else {
          dispatch({
            type: `REQUEST_${actionName}_ERROR`,
            payload: errorPayload,
          });
          if (opts.errorCallback)
            opts.errorCallback({ body: opts.body, error, dispatch });
        }
      }
      return Promise.reject(error);
    });
}

export const preApiCall = ({ dispatch, args }) => {
  const opts = { ...args };
  const actionName = args.action;
  if (args.preApiCallback) args.preApiCallback({ body: args.body, dispatch });
  if (actionName) {
    dispatch({ type: `REQUEST_${actionName}_INIT`, payload: args.initPayload });
  }
  return { opts: { ...opts }, actionName };
};

export const postApiCall = ({ body, dispatch, getStore, args, actionName }) => {
  if (body !== undefined && body !== null) {
    const processPayload = { body, dispatch, getStore };
    const process = args.process || ((t) => t);
    if (args.callback) {
      dispatch(args.callback(processPayload));
    }
    const actionDispatch = { type: actionName };
    const processedPayload = process(processPayload);
    if (processedPayload) {
      actionDispatch.payload = processedPayload;
    }
    if (actionName) {
      dispatch(actionDispatch);
      dispatch({
        type: `REQUEST_${actionName}_SUCCESS`,
        payload: args.successPayload,
      });
    }
  } else {
    dispatch({ type: API_ERROR, payload: { args } });
  }
};

export function cancelableRequest(args) {
  return async (dispatch, getStore) => {
    const { opts, actionName } = preApiCall({ dispatch, getStore, args });
    // Do NOT move this ternary into a helper function
    // because nested Promises with Redux dispatch can delay API calls.
    // Safe to refactor only after migrating away from Redux.
    const authTok = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();

    // Build axios config
    // Support both 'body' (preferred, consistent with doRequest) and 'data' (legacy) property names
    const requestData = opts.body !== undefined ? opts.body : opts.data;
    const axiosConfig = {
      url: opts.endpoint,
      method: opts.method,
      headers: {
        Authorization: authTok,
        'Accept-Language': i18n.language || 'en',
      },
      cancelToken: opts.cancelToken && new CancelToken(opts.cancelToken),
      ...(opts.signal && { signal: opts.signal }),
      ...(requestData !== undefined && { data: requestData }),
    };

    return axios(axiosConfig)
      .then((response) => {
        if (response.status === 200) {
          return postApiCall({
            body: response.data,
            dispatch,
            getStore,
            args,
            actionName,
          });
        }

        return Promise.reject('search request is canceled');
      })
      .catch((error) => {
        console.log(error);
        dispatch(args.errorCallback());
      });
  };
}

export function apiCall({ args }) {
  if (!args)
    console.error("'args' field in parameter for apiCall() is undefined");
  return (dispatch, getStore) => {
    const { opts, actionName } = preApiCall({ dispatch, getStore, args });

    const succeed = (body) => {
      postApiCall({ body, dispatch, getStore, args, actionName });
      return Promise.resolve(body);
    };

    const fail = (error) => {
      // `skipDeauth` (optional predicate on args) lets a caller suppress the
      // auto-deauth for THIS call based on current state — e.g. a stale,
      // overlapping bootstrap health check whose late UNAUTH must not sign out
      // an already-bootstrapped session. Other calls still deauth on UNAUTH.
      const skipDeauth =
        typeof args.skipDeauth === 'function'
          ? args.skipDeauth(getStore)
          : Boolean(args.skipDeauth);
      if (!skipDeauth) {
        validateAuthErrorAndDispatchDeauth(error, dispatch);
      }
      dispatch({ type: API_ERROR });
      dispatch({
        type: `REQUEST_${actionName}_ERROR`,
        payload: { error, body: args.body },
      });
      return Promise.reject(error);
    };

    return doRequest({ ...opts, dispatch, actionName })
      .then(succeed)
      .catch((error) => {
        // Retry-before-deauth (HMB-19203). On native (mobile WebView) a single
        // 401/403 used to log the user straight out — but that 401 can come
        // from a locally-valid access token the server rejects (refresh-token
        // rotation, revocation, or clock skew beyond the native skew margin).
        // Likewise, native token acquisition can fail before any request is
        // sent (NATIVE_AUTH_FAILURE thrown by getAccessToken). In both cases,
        // force ONE native token refresh and retry the request before deciding
        // to sign the user out.
        if (
          (error === UNAUTH || error === NATIVE_AUTH_FAILURE) &&
          shouldUseNativeAuth()
        ) {
          trackAuthEvent(datadogRumActions.DEAUTH_401, {
            action_name: actionName,
            phase: 'retry_attempt',
            trigger: error === UNAUTH ? 'http_401' : 'native_token_failure',
          });
          // Two distinct failure points must be classified separately:
          //  1. The FORCED REFRESH itself fails -> recovery failed (refresh
          //     token dead / native AUTH_ERROR) -> a genuine auth failure ->
          //     sign out. Treating it as transient would strand a truly-expired
          //     session on the retryable error screen.
          //  2. The refresh succeeds but the RETRIED REQUEST fails. A repeat
          //     UNAUTH, or a native token that STILL cannot be acquired
          //     (NATIVE_AUTH_FAILURE), is a confirmed auth failure -> sign out.
          //     A network/5xx here is transient -> keep the session.
          return getAccessToken(/* force */ true).then(
            (freshToken) =>
              // Hand the freshly-refreshed token straight to the retry so it
              // can't re-acquire (or read back) a stale one.
              doRequest({
                ...opts,
                dispatch,
                actionName,
                authTokenOverride: freshToken,
              })
                .then((body) => {
                  trackAuthEvent(datadogRumActions.DEAUTH_401_RECOVERED, {
                    action_name: actionName,
                  });
                  return succeed(body);
                })
                .catch((retryReqError) => {
                  // The retry reuses the forced-refresh token (authTokenOverride)
                  // and does NOT re-acquire, so it can only fail with a server
                  // 401 (UNAUTH -> deauth) or a transient network/5xx (keep the
                  // session). A NATIVE_AUTH_FAILURE can't originate here anymore.
                  trackAuthEvent(datadogRumActions.DEAUTH_401, {
                    action_name: actionName,
                    phase:
                      retryReqError === UNAUTH
                        ? 'retry_request_unauth'
                        : 'retry_request_transient',
                    error: String(retryReqError).slice(0, 200),
                  });
                  return fail(retryReqError);
                }),
            (refreshError) => {
              // Forced refresh failed after a confirmed 401 -> deauth.
              trackAuthEvent(datadogRumActions.DEAUTH_401, {
                action_name: actionName,
                phase: 'forced_refresh_failed',
                error: String(refreshError).slice(0, 200),
              });
              return fail(UNAUTH);
            },
          );
        }
        return fail(error);
      });
  };
}

export async function uploadFile(opts) {
  const url = localToRemoteUrl(opts.endpoint);
  const data = new FormData();
  data.append('file', opts.file);
  data.append('name', opts.name);
  // this ternary is used here to avoid a promise when possible
  // so that the api calls can be correctly parallelized,
  // please do not abstract the line below to a single promise helper function
  const authTok = shouldRenewAccessToken()
    ? await getAccessToken()
    : getOktaAccessToken();
  return axios({
    url,
    method: 'post',
    data,
    headers: {
      Authorization: authTok,
      'Accept-Language': i18n.language || 'en',
    },
    cancelToken: opts.cancelToken && new CancelToken(opts.cancelToken),
    ...(opts.signal && { signal: opts.signal }),
  });
}

export const getFile = async (url) => {
  const opts = {
    endpoint: url,
  };
  return doRequest(opts)
    .then((blob) => URL.createObjectURL(blob))
    .catch((err) => {
      console.error(err);
    });
};
